import { AuthUser } from '@bees-web/core/types'
import { readViewportSizeFromWindow } from '@bees-web/core/utils'
import { routeChangeHandler, routeInitialize } from '@bees-web/nfa-interactive-global'
import { CoreAnalytics } from '@segment/analytics-core'
import Cookies from 'js-cookie'
import singletonRouter, { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'

import store from '@/core/store'
import { Configuration } from '@/core/types'
import { createBrazeTrackingPlugin, useBraze } from '@/features/braze'
import { useOneTrust } from '@/features/onetrust'
import { usePendo } from '@/features/pendo'
import {
  //AnonymizationUtilityPlugin,
  createAnonymizeTrackPlugin,
  initialize,
} from '@/features/segment'

import { clearPersistStorage } from '../../utils/storage'
import { isDevelopment } from '../utils/environment'

const version = process.env.BUILD_BUILDID

interface UseAppTrackingParams {
  country: string
  pageKey: string
  userInfos?: AuthUser
  configuration?: Configuration
}

interface UseAppTrackingReturn {
  userId: string | null
  trackingConsented: boolean
  oneTrustInitialized: boolean
  brazeInitialized: boolean
}

const addNewRelicAttribute = (key: string, value: any) => {
  const newRelicAvailable =
    !isDevelopment &&
    !!process.env.NEW_RELIC_BROWSER_LICENSE_KEY &&
    !!process.env.NEW_RELIC_APPLICATION_ID &&
    !!window.NREUM

  if (key && newRelicAvailable) {
    window.NREUM.setCustomAttribute(key, value)
  }
}

const getDeviceResolution = (): string => {
  const size = readViewportSizeFromWindow()
  return size ? `${size.width}x${size.height}` : ''
}

const parseReferrerUrl = (referrer: string): string => {
  if (!referrer || referrer === '') return ''
  try {
    return new URL(referrer).pathname
  } catch {
    return ''
  }
}

const removeImpervaCookies = () => {
  if (process.env.STAGE !== 'prod') {
    const cookies = Cookies.get() || {}
    Object.keys(cookies).forEach((cookie) => {
      if (cookie.includes('incap_ses')) {
        Cookies.remove(cookie)
      }
    })
  }
}

/**
 * Hook unificado para inicialização de todas as ferramentas de tracking da aplicação
 * Combina funcionalidades anteriormente distribuídas entre useTracking e TrackingProvider
 */
const useAppTracking = ({
  country,
  pageKey,
  userInfos,
  configuration,
}: UseAppTrackingParams): UseAppTrackingReturn => {
  const router = useRouter()
  const { pathname } = router
  const { dispatch } = store

  const [userId, setUserId] = useState<string | null>(
    () => Cookies.get('ajs_user_id') || null
  )
  const [trackingConsented, setTrackingConsented] = useState<boolean>(false)
  const [oneTrustInitialized, setOneTrustInitialized] = useState<boolean>(
    () => typeof window !== 'undefined' && !!window.OneTrust
  )

  const {
    blockTrackersBeforeConsent = false,
    anonymizedSegmentProperties = [],
    brazeInAppConfiguration,
  } = useMemo(() => {
    return {
      blockTrackersBeforeConsent:
        configuration?.onetrust?.blockTrackersBeforeConsent ?? false,
      anonymizedSegmentProperties: configuration?.anonymizedSegmentProperties ?? [],
      brazeInAppConfiguration: configuration?.brazeInAppConfiguration,
    }
  }, [configuration])

  useEffect(() => {
    if (!globalThis.segment) globalThis.segment = {}
    /* eslint-disable-next-line security/detect-object-injection */
    if (!globalThis.segment[country])
      globalThis.segment = {
        ...globalThis.segment,
        ...initialize({ [country]: { segment: configuration?.segment } }),
      }

    removeImpervaCookies()
    clearPersistStorage(version)

    return routeChangeHandler(singletonRouter, store)
  }, [configuration?.segment, country])

  // OneTrust initialization check separado para evitar warning de dependência
  useEffect(() => {
    if (typeof window !== 'undefined' && window.OneTrust && !oneTrustInitialized) {
      setOneTrustInitialized(true)
    }
  }, [oneTrustInitialized])

  useEffect(() => {
    const referrer = document.referrer
    const origin = window.location.origin
    const currentPathname = window.location.pathname

    const referrerPath = parseReferrerUrl(referrer)
    dispatch(routeInitialize(origin, currentPathname, referrerPath))

    // New Relic attributes
    addNewRelicAttribute('journey', pageKey)
    addNewRelicAttribute('appVersion', version)
    const deviceResolution = getDeviceResolution()
    if (deviceResolution) {
      addNewRelicAttribute('deviceResolution', deviceResolution)
    }
  }, [dispatch, country, pageKey])

  // Inicialização e configuração do Segment Analytics
  useEffect(() => {
    // eslint-disable-next-line security/detect-object-injection
    if (globalThis.segment?.[country]) {
      // eslint-disable-next-line security/detect-object-injection
      const segment = globalThis.segment[country] as CoreAnalytics

      segment.register(
        //AnonymizationUtilityPlugin(country, anonymizedSegmentProperties),
        createAnonymizeTrackPlugin(anonymizedSegmentProperties),
        createBrazeTrackingPlugin(brazeInAppConfiguration)
      )

      globalThis.analytics = segment
    }
  }, [country, anonymizedSegmentProperties, brazeInAppConfiguration])

  // Inicialização dos hooks especializados de tracking
  useOneTrust({
    blockTrackersBeforeConsent,
    setTrackingConsented,
    setUserId,
    oneTrustInitialized,
    setOneTrustInitialized,
  })

  const brazeInitialized = useBraze({
    userId,
    country,
    pathname,
    trackingConsented,
    oneTrustInitialized,
    userInfos,
    configuration: configuration?.braze,
  })

  usePendo(userId)

  return {
    userId,
    trackingConsented,
    oneTrustInitialized,
    brazeInitialized,
  }
}

export default useAppTracking
